import { combineLatest } from 'rxjs';
import { first } from 'rxjs/operators';
import { FeedbackService } from './feedback.service';
import { Injectable } from '@angular/core';
import { MENU_ITEMS } from '../pages/pages-menu';
import { NbMenuItem } from '@nebular/theme';
import { PermissionService } from './permission.service';
import { Router } from '@angular/router';
import { SharedService } from './shared.service';
import { SurveyQuestionTypeEnum, UserPermissions } from '@howdy/models';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class MenuService {

  initialized = false;

  constructor(
    private router: Router,
    private sharedService: SharedService,
    private translate: TranslateService,
    private permissionService: PermissionService,
    private feedbackService: FeedbackService
  ) { }

  initializeMenu() {
    if (this.initialized) {
      return;
    }

    combineLatest([
      this.sharedService.onLanguageChange$,
      this.permissionService.userPermissions$
    ]).subscribe(() => {
      this.initialized = true;
      this.refreshMenuItems(this.permissionService.userPermissions);
    });
  }

  private async refreshMenuItems(permissions: UserPermissions) {
    const userPreference = this.sharedService.userPreferences;
    const navModel = MENU_ITEMS;
    const loadedTranslations = this.translate.translations[this.translate.currentLang];

    if (!loadedTranslations) {
      await this.translate.onDefaultLangChange.toPromise();
    }

    navModel.splice(0);
    navModel.length = 0;
    const surveys = await this.feedbackService.getCompanySurveys(true, true).pipe(first()).toPromise();

    const hideDepartmentsPage = !userPreference.activeCompanyId ||
      (!permissions.hasHowdyEmployeeRights &&
      !permissions.hasCompanyAdminReadonlyRights &&
      !permissions.hasOnlyDepartmentManagerRights);

    const orgChartPageLabel = this.sharedService.isOrgChartAllowed
      ? 'Portal.Shell.MainMenu.Dashboard.OrgChart.Title'
      : 'Portal.Shell.MainMenu.Dashboard.DrillDown.Title';
    const hideCustomSurveyReporting = this.sharedService.currentCompany?.Settings?.HideCustomSurveyReporting;

    navModel.unshift({
      title: this.translate.instant(orgChartPageLabel),
      icon: 'list-tree',
      link: '/pages/dashboard/orgchart',
      hidden: hideDepartmentsPage,
    });

    navModel.unshift({
      title: this.translate.instant('Portal.Shell.MainMenu.Dashboard.OtherSurveys.Title'),
      icon: 'clipboard-check',
      link: '/pages/dashboard/other',
      hidden: !surveys.filter(x => x.Questions?.every(q => q.StepType !== SurveyQuestionTypeEnum.Body)).length || hideCustomSurveyReporting
    });

    navModel.unshift({
      title: this.translate.instant('Portal.Shell.MainMenu.Dashboard.Body.Title'),
      icon: { icon: 'survey-body-icon', pack: 'kit' },
      link: '/pages/dashboard/body',
      hidden: !surveys.filter(x => x.Questions?.some(q => q.StepType === SurveyQuestionTypeEnum.Body)).length
    });

    navModel.unshift({
      title: this.translate.instant('Portal.Shell.MainMenu.Dashboard.Wellbeing.Title'),
      icon: 'brain',
      link: '/pages/dashboard/wellbeing',
      hidden: !userPreference.activeCompanyId
    });
    const isReportModuleEnabled = this.sharedService.currentCompany?.Settings?.ShowReportsModule;
    const disablePIIForManagers = this.sharedService.currentCompany?.Settings?.DisablePIIForManagers;

    const otherMenuItems: NbMenuItem[] = [{
      title: this.translate.instant('Portal.Shell.MainMenu.Company.Title'),
      icon: 'cog',
      hidden: !userPreference.activeCompanyId || permissions.hasOnlyDepartmentManagerRights || permissions.hasReadOnlyRights,
      children: [
        {
          title: this.translate.instant('Portal.Modules.Company.Tabs.Settings.Title'),
          link: '/pages/company/settings/company-settings',
        },
        {
          title: this.translate.instant('Portal.Modules.Company.Tabs.Integration.Title'),
          link: '/pages/company/settings/integrations',
        },
        {
          title: this.translate.instant('Portal.Modules.Company.Tabs.APICalls.Title'),
          link: '/pages/company/settings/api-calls',
        }
      ]
    },
    {
      title: this.translate.instant('Portal.Shell.MainMenu.Employees.Title'),
      icon: 'user',
      link: '/pages/employee',
      hidden: !userPreference.activeCompanyId
        || permissions.hasOnlyObserverRights
        || (permissions.hasOnlyDepartmentManagerRights && disablePIIForManagers)
        || (permissions.hasCompanyAdminReadonlyRights && permissions.hasReadOnlyRights)
    },
    {
      title: this.translate.instant('Portal.Shell.MainMenu.Reports.Title'),
      icon: 'file',
      hidden: !(isReportModuleEnabled && userPreference.activeCompanyId && permissions.hasCompanyAdminRights),
      children: [
        {
          title: this.translate.instant('Portal.Shell.MainMenu.Reports.Overview.Title'),
          link: '/pages/report/overview',
        },
        {
          title: this.translate.instant('Portal.Shell.MainMenu.Reports.AwaitingApproval.Title'),
          link: '/pages/report/awaitingapproval',
        }
      ]
    },
    {
      title: this.translate.instant('Portal.Shell.MainMenu.Users.Title'),
      icon: 'user-lock',
      link: '/pages/user',
      hidden: !((permissions.hasUserAdminRights || permissions.hasHowdyEmployeeRights) && userPreference.activeCompanyId)
    },
    {
      title: this.translate.instant('Portal.Shell.MainMenu.Feedback'),
      icon: 'clipboard-check',
      link: '/pages/feedback',
      hidden: !(userPreference.activeCompanyId && (permissions.hasCompanyAdminRights || permissions.hasHowdyEmployeeRights))
    },
    {
      title: this.translate.instant('Portal.Shell.MainMenu.Resources.Title'),
      icon: 'lightbulb',
      link: '/pages/resources',
      hidden: !userPreference.activeCompanyId
    },
    {
      title: 'Administration',
      group: true,
      hidden: !permissions.hasSystemAdminRights || !permissions.hasHowdyEmployeeRights
    },
    {
      title: 'Tools',
      icon: 'tools',
      hidden: !(permissions.hasSystemAdminRights || permissions.hasHowdyEmployeeRights),
      children: [
        {
          title: this.translate.instant('Portal.Shell.MainMenu.CompanyList.Title'),
          link: '/pages/admin/company-list'
        },
        {
          title: this.translate.instant('Portal.Shell.MainMenu.LabelEditor.Title'),
          link: '/pages/admin/l10neditor'
        },
        {
          title: this.translate.instant('Portal.Shell.MainMenu.SettingsEditor.Title'),
          link: '/pages/admin/settings-list',
          hidden: !permissions.hasSystemAdminRights
        },
        {
          title: 'Push notification',
          link: '/pages/admin/push-notification',
          hidden: !userPreference.activeCompanyId || !permissions.hasSystemAdminRights
        },
        {
          title: 'Email test',
          link: '/pages/admin/email-test',
          hidden: !permissions.hasResponseCenterAdminRights
        },
        {
          title: 'Intervention rating',
          link: '/pages/admin/intervention-test',
          hidden: !(permissions.hasResponseCenterAdminRights || permissions.hasHowdyEmployeeRights)
        },
        {
          title: 'Create coaching cases',
          link: '/pages/admin/manager-incident',
          hidden: !permissions.hasResponseCenterAdminRights
        },
        {
          title: 'Coaching overview',
          link: '/pages/admin/manager-coaching',
          hidden: !(permissions.hasResponseCenterAdminRights || permissions.hasHowdyEmployeeRights)
        },
        {
          title: 'Activity statistics',
          link: '/pages/admin/statistics',
          hidden: !permissions.hasSystemAdminRights
        }
      ]
    }];

    this.fixSelectionAfterPageReload(otherMenuItems);
    this.setParentOfAllChildrenMenuItems(otherMenuItems);

    if (!!userPreference.activeResponseCenterId) {
      if (navModel.find(item => item.link === '/responsecenter/incidents/overview')) {
        return;
      }

      navModel.push({
        title: this.translate.instant('Portal.Modules.Incidents.Title'),
        icon: 'chart-line',
        hidden: !userPreference.activeResponseCenterId,
        link: '/responsecenter/incidents/overview'
      });
    } else {
      if (navModel.find(item => item.link === '/pages/dashboard/overview')) {
        return;
      }

      navModel.push(...otherMenuItems);
    }
  }

  private fixSelectionAfterPageReload(otherMenuItems: NbMenuItem[]) {
    const activeUrl = this.router.url;

    otherMenuItems.forEach(item => {
      if (!!item?.link) {
        item['selected'] = activeUrl === item.link;
      } else {
        item.children?.forEach(child => {
          child['selected'] = activeUrl === child?.link;
        });
      }
    });
  }

  private setParentOfAllChildrenMenuItems(menuItems: NbMenuItem[]) {
    menuItems.forEach(item => {
      this.setParent(item);
    });
  }

  private setParent(item: NbMenuItem) {
    if (item.children) {
      item.children.forEach(child => {
        child.parent = item;
        this.setParent(child);
      });
    }
  }
}
